import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FiX, FiRefreshCw } from 'react-icons/fi';

const MAX_VISIBLE_OPTIONS = 80;

export interface SearchableOption {
  value: string;
  label: string;
  meta?: Record<string, unknown>;
}

interface SearchableSelectProps {
  options: SearchableOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  title?: string;
  clearable?: boolean;
  isLoading?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Type to search...',
  disabled = false,
  required = false,
  className = '',
  title,
  clearable = false,
  isLoading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((o) => o.value === value);
  const displayValue = selectedOption ? selectedOption.label : '';
  const effectiveDisabled = disabled || isLoading;
  const showClear = clearable && !!value && !effectiveDisabled;

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase().trim();
    return options.filter((opt) => opt.label.toLowerCase().includes(term));
  }, [options, searchTerm]);

  const visibleOptions = useMemo(() => {
    const slice = filteredOptions.slice(0, MAX_VISIBLE_OPTIONS);
    if (value) {
      const idx = filteredOptions.findIndex((o) => o.value === value);
      if (idx >= 0 && idx >= MAX_VISIBLE_OPTIONS) {
        const selected = filteredOptions[idx];
        return [selected, ...slice.filter((o) => o.value !== value)].slice(0, MAX_VISIBLE_OPTIONS);
      }
    }
    return slice;
  }, [filteredOptions, value]);
  const hasMore = filteredOptions.length > MAX_VISIBLE_OPTIONS;
  const totalCount = filteredOptions.length;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFocus = () => {
    if (!disabled) {
      setIsOpen(true);
      setSearchTerm('');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleSelect = (e: React.MouseEvent, opt: SearchableOption) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('[SearchableSelect] Brand selected:', {
      value: opt.value,
      label: opt.label,
      meta: opt.meta,
      hasMeta: !!opt.meta,
      allOptionKeys: opt.meta ? Object.keys(opt.meta) : [],
    });
    onChange(opt.value);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    onChange('');
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const effectivePlaceholder = isLoading ? 'Loading brands...' : placeholder;

  return (
    <div
      ref={containerRef}
      className={`searchable-select ${className} ${effectiveDisabled ? 'disabled' : ''} ${showClear ? 'has-clear' : ''} ${isLoading ? 'loading' : ''}`}
    >
      {isLoading && (
        <span className="searchable-select-spinner" aria-hidden>
          <FiRefreshCw size={16} className="spin" />
        </span>
      )}
      <div className="searchable-select-inner">
        <input
          type="text"
          value={isOpen ? searchTerm : displayValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={effectivePlaceholder}
          disabled={effectiveDisabled}
          required={required && !value}
          title={title || (required && !value ? 'Please fill out this field' : '')}
          className="searchable-select-input"
          autoComplete="off"
        />
        {showClear && (
          <button
            type="button"
            className="searchable-select-clear"
            onClick={handleClear}
            title="Clear selection"
            aria-label="Clear selection"
          >
            <FiX size={16} />
          </button>
        )}
      </div>
      {isOpen && (
        <div className="searchable-select-dropdown">
          {isLoading ? (
            <div className="searchable-select-loading">Loading brands...</div>
          ) : visibleOptions.length > 0 ? (
            <>
              {visibleOptions.map((opt, idx) => (
                <div
                  key={`${opt.value}-${idx}`}
                  className={`searchable-select-option ${opt.value === value ? 'selected' : ''}`}
                  onMouseDown={(e) => handleSelect(e, opt)}
                  role="option"
                  aria-selected={opt.value === value}
                >
                  {opt.label}
                </div>
              ))}
              {hasMore && (
                <div className="searchable-select-more">
                  Showing first {MAX_VISIBLE_OPTIONS} of {totalCount}. Type to narrow down.
                </div>
              )}
            </>
          ) : (
            <div className="searchable-select-empty">No matches</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
